/**
 * useNetworkStatus - Hook for detecting network connectivity
 * Provides online/offline status and network error handling
 */

import { useState, useEffect } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  isOffline: boolean;
  isSlowConnection: boolean;
  connectionType: string | null;
}

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSlowConnection, setIsSlowConnection] = useState(false);
  const [connectionType, setConnectionType] = useState<string | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    // Check connection type and speed if available
    const checkConnectionSpeed = () => {
      // @ts-ignore - navigator.connection is experimental
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      if (connection) {
        setConnectionType(connection.effectiveType || connection.type || null);
        
        // Consider 2g and slow-2g as slow connections
        const slowConnections = ['slow-2g', '2g'];
        setIsSlowConnection(slowConnections.includes(connection.effectiveType));
        
        // Listen for connection changes
        const handleConnectionChange = () => {
          setConnectionType(connection.effectiveType || connection.type || null);
          setIsSlowConnection(slowConnections.includes(connection.effectiveType));
        };
        
        connection.addEventListener('change', handleConnectionChange);
        
        return () => {
          connection.removeEventListener('change', handleConnectionChange);
        };
      }
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check connection speed
    const cleanup = checkConnectionSpeed();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (cleanup) cleanup();
    };
  }, []);

  return {
    isOnline,
    isOffline: !isOnline,
    isSlowConnection,
    connectionType,
  };
}